/**
 * Unit Allocation Engine — slot-based allocation per the Shakti Power ERP
 * "Unit Allocation Engine" Functional Specification (v1.0).
 *
 * Goal: allocate every generated unit to mapped consumers following Open-Access
 * slot transfer rules and consumer consumption capacity, minimising lapse.
 */

export type Slot = 'C1' | 'C2' | 'C3' | 'C4' | 'C5'
export const SLOTS: Slot[] = ['C1', 'C2', 'C3', 'C4', 'C5']

/** Display times for each Open-Access slot (spec §3). */
export const SLOT_TIMES: Record<Slot, string> = {
  C1: '06:00 AM – 10:00 AM',
  C2: '06:00 PM – 10:00 PM',
  C3: 'Normal Hours',
  C4: '10:00 AM – 06:00 PM & 05:00 AM – 06:00 AM',
  C5: '10:00 PM – 05:00 AM',
}

/** Allowed consumption slots for units GENERATED in a given slot (spec §4). */
export const SLOT_TRANSFER: Record<Slot, Slot[]> = {
  C1: ['C1', 'C2', 'C4', 'C5'],
  C2: ['C2', 'C1', 'C4', 'C5'],
  C3: ['C3', 'C2', 'C1'],
  C4: ['C4', 'C5'],
  C5: ['C5'],
}

export type SlotUnits = Record<Slot, number>
export const emptySlots = (): SlotUnits => ({ C1: 0, C2: 0, C3: 0, C4: 0, C5: 0 })
export const slotTotal = (s: SlotUnits): number => SLOTS.reduce((t, k) => t + (Number(s[k]) || 0), 0)

export interface EngineConsumer {
  id: string
  name: string
  sharePct: number          // contracted annual allocation %
  consumption: SlotUnits     // actual metered consumption per slot (capacity)
}

export interface AllocationCell {
  consumerId: string
  consumerName: string
  genSlot: Slot              // slot the units were generated in
  consumeSlot: Slot          // slot the consumer absorbs them in
  units: number
  priority: 1 | 2 | 3 | 4    // which allocation priority placed these units
}

export interface AuditEntry {
  fromConsumer: string
  toConsumer: string
  fromSlot: Slot
  toSlot: Slot
  units: number
  reason: string
}

export interface ConsumerTotals {
  consumerId: string
  consumerName: string
  slots: SlotUnits
  total: number
}

export interface AllocationResult {
  initialByShare: ConsumerTotals[]   // §6 initial allocation (uncapped, by share)
  finalByConsumer: ConsumerTotals[]  // final allocation after redistribution
  finalCells: AllocationCell[]
  audit: AuditEntry[]                // §13 every redistribution movement
  lapseBySlot: SlotUnits
  totalGenerated: number
  totalAllocated: number
  totalLapse: number
}

/**
 * Run the full allocation: initial by-share fill, then priority-based
 * redistribution of every unit a consumer can't absorb in its slot.
 *
 * Priority (spec §9):
 *   P1 same consumer, same slot
 *   P2 same consumer, allowed alternate slot
 *   P3 other consumer, same slot
 *   P4 other consumer, allowed alternate slot
 *   P5 lapse
 */
export function runAllocation(
  generation: Record<string, number>,
  consumers: {
    id: string
    name: string
    sharePct: number
    consumption: Record<string, number>
  }[],
  activeSlots: string[] = ['C1', 'C2', 'C4', 'C5'],
  slotTransfer: Record<string, string[]> = SLOT_TRANSFER
): AllocationResult {
  const remCap: Record<string, Record<string, number>> = {}
  consumers.forEach(c => { remCap[c.id] = { ...c.consumption } })

  const finalCells: AllocationCell[] = []
  const audit: AuditEntry[] = []

  const createEmptyUnits = () => {
    const u: Record<string, number> = {}
    activeSlots.forEach(s => { u[s] = 0 })
    return u
  }
  const lapseBySlot = createEmptyUnits()

  // How much more can still be CONSUMED in each slot without exceeding what was
  // actually generated in that slot — separate from remCap (a consumer's own
  // metered headroom). A same-consumer/other-consumer alternate-slot transfer
  // (P2/P3/P4) must respect both: a consumer's meter reading is one ceiling,
  // but the target slot only ever has as many real units to hand out as were
  // generated there in the first place. Skipping this second ceiling let a
  // transfer push a slot's total allocation past its own generation — a
  // physically impossible state the UI then had to flag as "EXCEEDS".
  const remGenSlotCap: Record<string, number> = {}
  activeSlots.forEach(s => { remGenSlotCap[s] = generation[s] || 0 })

  const place = (c: any, genSlot: string, consumeSlot: string, units: number, priority: 1 | 2 | 3 | 4) => {
    if (units <= 0) return
    finalCells.push({
      consumerId: c.id,
      consumerName: c.name,
      genSlot: genSlot as Slot,
      consumeSlot: consumeSlot as Slot,
      units,
      priority,
    })
    remCap[c.id][consumeSlot] -= units
    remGenSlotCap[consumeSlot] = (remGenSlotCap[consumeSlot] || 0) - units
  }

  // Helper to sum a record of slots
  const sumRecord = (r: Record<string, number>) => activeSlots.reduce((t, k) => t + (Number(r[k]) || 0), 0)

  // Split a slot total across consumers by share %, as integers.
  // Shares are scaled so they always sum to exactly 100% of the active consumers,
  // preventing any initial unclaimed generation lapse.
  const splitByShare = (total: number, cons: typeof consumers): number[] => {
    if (cons.length === 0) return []
    const totalSharePct = cons.reduce((s, c) => s + (Number(c.sharePct) || 0), 0)
    const scale = totalSharePct > 0 ? 100 / totalSharePct : 1
    const raw = cons.map(c => (total * (Number(c.sharePct) || 0) * scale) / 100)
    const out = raw.map(Math.floor)
    const claimedTotal = total
    let rem = claimedTotal - out.reduce((s, n) => s + n, 0)
    const byFrac = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac)
    for (let k = 0; rem > 0 && k < byFrac.length; k++, rem--) out[byFrac[k].i] += 1
    return out
  }

  // ── Initial allocation by share + P1 same-slot fill ──
  const initialByShare: ConsumerTotals[] = consumers.map(c => ({
    consumerId: c.id,
    consumerName: c.name,
    slots: createEmptyUnits() as SlotUnits,
    total: 0,
  }))
  const leftovers: { oc: typeof consumers[0]; gs: string; units: number }[] = []

  activeSlots.forEach(gs => {
    const slotGen = generation[gs] || 0
    const shares = splitByShare(slotGen, consumers)
    const unclaimed = slotGen - shares.reduce((s, n) => s + n, 0)
    if (unclaimed > 0) {
      // distribute unclaimed to consumers proportionately
      const unclaimedShares = splitByShare(unclaimed, consumers)
      consumers.forEach((c, idx) => {
        shares[idx] += unclaimedShares[idx]
      })
    }
    consumers.forEach((c, idx) => {
      const su = shares[idx]
      const disp = initialByShare[idx]
      disp.slots[gs as Slot] = su
      disp.total += su
      const placed = Math.min(su, remCap[c.id][gs] || 0)     // P1: same consumer, same slot
      place(c, gs, gs, placed, 1)
      if (su - placed > 0) leftovers.push({ oc: c, gs, units: su - placed })
    })
  })

  // ── P2: same consumer, allowed alternate slots ──
  const fillSelfAlt = (oc: typeof consumers[0], gs: string, units: number): number => {
    let rem = units
    const transfers = slotTransfer[gs] || [gs]
    for (const cs of transfers) {
      if (cs === gs || rem <= 0) continue
      const give = Math.min(rem, remCap[oc.id][cs] || 0, remGenSlotCap[cs] || 0)
      if (give > 0) {
        place(oc, gs, cs, give, 2)
        audit.push({ fromConsumer: oc.name, toConsumer: oc.name, fromSlot: gs as Slot, toSlot: cs as Slot, units: give, reason: 'P2 · same consumer, alternate slot' })
        rem -= give
      }
    }
    return rem
  }

  // ── P3 / P4: other consumers, equal water-fill distribution ──
  const fillOthers = (oc: typeof consumers[0], gs: string, targetSlots: string[], priority: 3 | 4, units: number): number => {
    let rem = units
    const targets = consumers.filter(c => c.id !== oc.id)
    while (rem > 0) {
      const avail = targets.filter(t => targetSlots.some(cs => (remCap[t.id][cs] || 0) > 0 && (remGenSlotCap[cs] || 0) > 0))
      if (avail.length === 0) break
      const per = Math.max(1, Math.floor(rem / avail.length))
      let progressed = false
      for (const t of avail) {
        if (rem <= 0) break
        let need = Math.min(per, rem)
        for (const cs of targetSlots) {
          if (need <= 0) break
          const give = Math.min(need, remCap[t.id][cs] || 0, remGenSlotCap[cs] || 0)
          if (give > 0) {
            place(t, gs, cs, give, priority)
            audit.push({
              fromConsumer: oc.name,
              toConsumer: t.name,
              fromSlot: gs as Slot,
              toSlot: cs as Slot,
              units: give,
              reason: priority === 3 ? 'P3 · other consumer, same slot' : 'P4 · other consumer, alternate slot',
            })
            need -= give; rem -= give; progressed = true
          }
        }
      }
      if (!progressed) break
    }
    return rem
  }

  leftovers.forEach(({ oc, gs, units }) => {
    let rem = fillSelfAlt(oc, gs, units)
    if (rem > 0) rem = fillOthers(oc, gs, [gs], 3, rem)
    if (rem > 0) {
      const transfers = slotTransfer[gs] || [gs]
      rem = fillOthers(oc, gs, transfers.filter(s => s !== gs), 4, rem)
    }
    if (rem > 0) {
      // No consumer had metered headroom — in this slot or any transfer-eligible
      // alternate slot that still had generation left to give — to absorb these
      // units. They genuinely lapse. Previously this force-crammed the remainder
      // back onto a consumer regardless of their meter reading just to keep
      // totalLapse at 0; that hid real shortfalls behind a fake "fully allocated"
      // number instead of surfacing them as lapse.
      lapseBySlot[gs] = (lapseBySlot[gs] || 0) + rem
      audit.push({
        fromConsumer: oc.name,
        toConsumer: '—',
        fromSlot: gs as Slot,
        toSlot: gs as Slot,
        units: rem,
        reason: 'Lapsed — no consumer had metered headroom to absorb these units',
      })
    }
  })

  // ── Aggregate ──
  const finalByConsumer: ConsumerTotals[] = consumers.map(c => ({
    consumerId: c.id,
    consumerName: c.name,
    slots: createEmptyUnits() as SlotUnits,
    total: 0,
  }))
  finalCells.forEach(cell => {
    const row = finalByConsumer.find(r => r.consumerId === cell.consumerId)!
    row.slots[cell.consumeSlot] += cell.units
    row.total += cell.units
  })

  const totalGenerated = sumRecord(generation)
  const totalLapse = sumRecord(lapseBySlot)
  return {
    initialByShare,
    finalByConsumer,
    finalCells,
    audit,
    lapseBySlot: lapseBySlot as SlotUnits,
    totalGenerated,
    totalAllocated: totalGenerated - totalLapse,
    totalLapse,
  }
}
